import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useApi } from '../../hooks/useApi';
import {
  LayoutDashboard, Send, Inbox, BarChart2, Smartphone,
  FileText, BookOpen, Package, CreditCard, LogOut,
  Users, Building, DollarSign, ChevronLeft, ChevronRight,
  Sparkles, AlertCircle, Bell,
} from 'lucide-react';
import './Sidebar.css';

const Sidebar = ({ mobileOpen, onMobileClose, collapsed, onCollapse }) => {
  const { user, logout, isSuperAdmin } = useAuth();
  const { request } = useApi();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [escalationCount, setEscalationCount] = useState(0);

  const orgName = localStorage.getItem('wa_org_name') || user?.organizationName || user?.orgName || 'Organization';

  const fetchUnreadCount = async () => {
    if (isSuperAdmin) return;
    try {
      const conversations = await request('get', '/api/inbox/conversations').catch(() => []);
      const total = conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
      setUnreadCount(total);
    } catch {}
  };

  const fetchEscalationCount = async () => {
    if (isSuperAdmin) return;
    try {
      const response = await request('get', '/api/inbox/conversations/escalated/count').catch(() => ({ count: 0 }));
      setEscalationCount(response.count || 0);
    } catch {}
  };

  useEffect(() => { 
    fetchUnreadCount(); 
    fetchEscalationCount(); 
  }, []);

  useEffect(() => {
    if (isSuperAdmin) return;
    const t = setInterval(() => {
      fetchUnreadCount();
      fetchEscalationCount();
    }, 10000);
    return () => clearInterval(t);
  }, [isSuperAdmin]);

  const orgAdminNav = [
    { group: 'Main', items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/send', icon: Send, label: 'Send Campaign' },
      { to: '/inbox', icon: Inbox, label: 'Inbox', badge: unreadCount },
      { to: '/escalation', icon: AlertCircle, label: 'Escalations', badge: escalationCount },
      { to: '/reports', icon: BarChart2, label: 'Reports' },
    ]},
    { group: 'Setup', items: [
      { to: '/onboarding', icon: Smartphone, label: 'WA Onboarding' },
      { to: '/templates', icon: FileText, label: 'Templates' },
      { to: '/phonebooks', icon: BookOpen, label: 'Phonebooks' },
      { to: '/products', icon: Package, label: 'Products' },
    ]},
    { group: 'Automation', items: [
      { to: '/ai-config', icon: Sparkles, label: 'AI Assistant' },
      { to: '/reminder-settings', icon: Bell, label: 'Reminders' },
    ]},
    { group: 'Account', items: [
      { to: '/billing', icon: CreditCard, label: 'Billing' },
    ]},
  ];

  const superAdminNav = [
    { group: 'Main', items: [
      { to: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/admin/pricing', icon: DollarSign, label: 'Set Prices' },
    ]},
    { group: 'Management', items: [
      { to: '/admin/organizations', icon: Building, label: 'Organizations' },
      { to: '/admin/users', icon: Users, label: 'Users' },
      { to: '/admin/credits', icon: DollarSign, label: 'Credits' },
      { to: '/admin/payments', icon: CreditCard, label: 'All Payments' },
    ]},
  ];

  const navigation = isSuperAdmin ? superAdminNav : orgAdminNav;
  const displayName = isSuperAdmin ? 'Super Admin' : (user?.name || 'User');
  const displaySub = isSuperAdmin ? 'System Administrator' : (orgName || 'Organization');
  const activeColor = isSuperAdmin ? 'var(--primary)' : 'var(--accent)';
  const activeBg = isSuperAdmin ? 'var(--primary-bg)' : 'var(--accent-bg)';

  const handleLogout = () => { logout(); navigate('/login'); };

  const NavItems = ({ isMobile = false }) => (
    <nav className={isMobile ? 'sidebar-mobile-nav' : 'sidebar-nav'}>
      {navigation.map(section => (
        <div key={section.group} className={isMobile ? '' : 'sidebar-section'}>
          {(!collapsed || isMobile) && (
            <div className={isMobile ? 'sidebar-mobile-section-label' : 'sidebar-section-label'}>
              {section.group}
            </div>
          )}
          {section.items.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              data-label={item.label}
              className={({ isActive }) =>
                [
                  isMobile ? 'sidebar-mobile-link' : 'sidebar-link',
                  isActive ? 'active' : '',
                  (!isMobile && collapsed) ? 'link-collapsed' : '',
                ].filter(Boolean).join(' ')
              }
              style={{ '--active-color': activeColor, '--active-bg': activeBg }}
              onClick={isMobile ? onMobileClose : undefined}
            >
              <item.icon className={isMobile ? 'sidebar-mobile-link-icon' : 'sidebar-link-icon'} />
              {(!collapsed || isMobile) && <span className="sidebar-link-label">{item.label}</span>}
              {(!collapsed || isMobile) && item.badge > 0 && (
                <span className={isMobile ? 'sidebar-mobile-link-badge' : 'sidebar-link-badge'}>
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
              {collapsed && !isMobile && item.badge > 0 && (
                <span className="sidebar-link-badge-dot" />
              )}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );

  return (
    <>
      <aside className={`sidebar ${collapsed ? 'sidebar-collapsed' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className={`sidebar-logo-icon${isSuperAdmin ? ' super-admin' : ''}`}>
              {isSuperAdmin ? 'S' : 'J'}
            </div>
            {!collapsed && (
              <div className="sidebar-logo-text">
                <span className="sidebar-brand">{isSuperAdmin ? 'Jipate WA Admin' : 'Jipate'}</span>
                <span className="sidebar-tagline">{isSuperAdmin ? 'System Management' : 'Business Platform'}</span>
              </div>
            )}
          </div>
          <button className="sidebar-collapse-btn" onClick={onCollapse} title={collapsed ? 'Expand' : 'Collapse'}>
            {collapsed
              ? <ChevronRight style={{ width: 14, height: 14 }} />
              : <ChevronLeft style={{ width: 14, height: 14 }} />}
          </button>
        </div>

        {isSuperAdmin && !collapsed && <div className="sidebar-badge">🔐 Super Admin</div>}

        <NavItems />

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-avatar" style={isSuperAdmin ? { background: 'var(--primary)' } : {}}>
              {displayName[0]?.toUpperCase() || 'U'}
            </div>
            {!collapsed && (
              <div className="sidebar-user-info">
                <div className="sidebar-user-name">{displayName}</div>
                <div className="sidebar-user-role">{displaySub}</div>
              </div>
            )}
            <button onClick={handleLogout} className="sidebar-logout-btn" title="Log out">
              <LogOut style={{ width: 15, height: 15 }} />
            </button>
          </div>
        </div>
      </aside>

      <div className={`sidebar-mobile${mobileOpen ? ' open' : ''}`}>
        <div className="sidebar-mobile-header">
          <div className="sidebar-mobile-logo">
            <div className={`sidebar-logo-icon${isSuperAdmin ? ' super-admin' : ''}`}>
              {isSuperAdmin ? 'S' : 'J'}
            </div>
            <span>{isSuperAdmin ? 'Jipate Admin' : 'Jipate'}</span>
          </div>
          <button className="sidebar-mobile-close" onClick={onMobileClose}>
            <ChevronLeft style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {isSuperAdmin && <div className="sidebar-badge">🔐 Super Admin</div>}

        <NavItems isMobile />

        <div className="sidebar-mobile-footer">
          <div className="sidebar-mobile-user">
            <div className="sidebar-avatar" style={isSuperAdmin ? { background: 'var(--primary)' } : {}}>
              {displayName[0]?.toUpperCase() || 'U'}
            </div>
            <div className="sidebar-mobile-user-info">
              <div className="sidebar-mobile-user-name">{displayName}</div>
              <div className="sidebar-mobile-user-role">{displaySub}</div>
            </div>
          </div>
          <button onClick={handleLogout} className="sidebar-mobile-logout">
            <LogOut style={{ width: 15, height: 15 }} />
            Log out
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;